import { useState } from "react";
import { format } from "date-fns";
import { useAuth } from "react-oidc-context";
import { Controller, useForm } from "react-hook-form";

import ErrorIcon from "@mui/icons-material/Error";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";

import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { TeamsIcon } from "../../../../../../icons/Teams";
import MonthPicker from "../../../../../addFOBIndicationPanel/components/monthPicker";
import { useNitroStore } from "../../../../../../store/Store";
import ListField from "../../../../../../components/ListField";
import IIndicationsRequest from "../../../../../addFOBIndicationPanel/types/IIndicationsRequest";
import cookie from "../../../../../../store/cookie";
import { IndicationsSchema } from "../../../../../addFOBIndicationPanel/schemas/indicationSchema";
import NumberTextfield from "../../../../../../components/NumberTextfield";
import NitroService from "../../../../../../utils/nitro.service";

import "./index.css";
import TeamsMessage from "../../../../../addFOBIndicationPanel/lib/teamsMessage";
import LoadingIcon from "../../../../../../components/Loading";
import CompanyFilter from "../../../../../../components/filters/CompanyFilter";
import useValidationResolve from "../../../../../../hooks/useValidationResolve";
import ICompAnalysis from "../../../../../../types/ICompAnalysis";
import { DeliveryIcon, FOBIcon } from "../../../../../../icons";
import IndicationTypeButton from "./IndicationTypeButton";
import { useIsMobile } from "../../../../../../hooks/useIsMobile";

const now = new Date();

const NewIndication = ({
  competitor,
  reload,
  close,
  newIndication,
}: {
  competitor: ICompAnalysis;
  reload: () => void;
  close: () => void;
  newIndication: {
    offer: number;
    freight: number;
    netback: number;
  } | null;
}) => {
  const auth = useAuth();
  const [
    mode,
    product,
    savedNotification,
    failedNotification,
    selectedLocation,
  ] = useNitroStore((state) => [
    state.mode,
    state.product,
    state.savedNotification,
    state.failedNotification,
    state.selectedLocation,
  ]);

  const disablePostToTeams = useIsMobile(768);

  const [indicationType, setIndicationType] = useState<
    "FOB" | "DELIVERED" | undefined
  >(undefined);
  const form = useForm<IIndicationsRequest>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: useValidationResolve(IndicationsSchema),
    defaultValues: {
      contactName: "",
      party: selectedLocation?.owner,
      marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
      currency: cookie.currency,
      uom: cookie.uom,
      indications: [
        {
          locationId: competitor.origin.id,
          type: "DELIVERED",
          priceType: "single",
          product,
          mode,
          modes: [mode],
          competitor: "",
          quantity: 0,
          startDate: format(
            new Date(now.getFullYear(), now.getMonth(), 1),
            "yyyy/MM/dd"
          ).replaceAll("/", "-"),
          endDate: format(
            new Date(now.getFullYear(), now.getMonth() + 1, 0),
            "yyyy/MM/dd"
          ).replaceAll("/", "-"),
          comments: "",
          postToTeams: true,
          prices: [
            {
              origin: competitor.origin.name,
              destination: competitor.destination.name,
              fobPrice: newIndication?.netback || 0,
              freight:
                newIndication?.freight || Number(competitor.freight || 0),
              deliveredPrice:
                newIndication?.offer ||
                Number(competitor.freight || 0),
            },
          ],
        },
      ],
      isKoch: false,
    },
  });

  const logIndication = async (record: IIndicationsRequest) => {
    try {
      // https://support.microsoft.com/en-us/office/use-markdown-formatting-in-microsoft-teams-4d10bd65-55e2-4b2d-a1f3-2bebdcd2c772

      //  Attachment or html options here
      // https://learn.microsoft.com/en-us/graph/api/chatmessage-post?view=graph-rest-1.0&tabs=http

      // ################################################################################################################################
      // ## POST TO TEAMS ###############################################################################################################
      // ################################################################################################################################
      if (!disablePostToTeams && record.indications[0].postToTeams) {
        try {
          await TeamsMessage.postIndication(record);

          savedNotification("Indication posted to Teams.");
        } catch (err) {
          console.error("Failed to post indication to Teams", err);
          failedNotification(
            "Failed to post indication to Teams.",
            (err as any).message
          );
          throw new Error("Failed to post indication to Teams.");
        }
      }

      // ################################################################################################################################
      // ## SAVE INDICATION #############################################################################################################
      // ################################################################################################################################
      try {
        await NitroService.createIndications(record);
        savedNotification("Successfully created indication.");
      } catch (err) {
        console.error("Failed to create indication", err);
        throw new Error("Failed to create indication.");
      }

      reload();
      close();
    } catch (err) {
      failedNotification("Failed to create indication.", (err as any).message);
    }
  };

  const changePrice = (price: number, type: "FOB" | "DELIVERED") => {
    const freight = form.getValues("indications.0.prices.0.freight");
    setIndicationType(type);
    form.setValue("indications.0.type", type);
    if (type === "FOB") {
      form.setValue(
        "indications.0.prices.0.deliveredPrice",
        Math.round((Number(price) + Number(freight)) * 100) / 100
      );
      form.setValue(
        "indications.0.prices.0.fobPrice",
        Math.round((Number(price) * 100) / 100)
      );
    } else {
      form.setValue("indications.0.prices.0.deliveredPrice", price);
      form.setValue(
        "indications.0.prices.0.fobPrice",
        Math.round((Number(price) - Number(freight)) * 100) / 100
      );
    }
  };

  const changeFreight = (value: number) => {
    form.setValue("indications.0.prices.0.freight", value);
    if (indicationType === "FOB") {
      const fobPrice = form.getValues("indications.0.prices.0.fobPrice") || 0;
      form.setValue(
        "indications.0.prices.0.deliveredPrice",
        Math.round((fobPrice + Number(value)) * 100) / 100
      );
    } else {
      const deliveredPrice =
        form.getValues("indications.0.prices.0.deliveredPrice") || 0;
      form.setValue(
        "indications.0.prices.0.fobPrice",
        Math.round((deliveredPrice - Number(value)) * 100) / 100
      );
    }
  };

  if (!indicationType) {
    return (
      <div style={{ display: "flex", flexDirection: "column", marginTop: 40 }}>
        <div style={{ textAlign: "center", fontWeight: 500, fontSize: 28 }}>
          New Indication
        </div>
        <div style={{ textAlign: "center", color: "rgb(152,152,152)" }}>
          Choose the type of indication to add
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 20,
            marginTop: 30,
          }}
        >
          <IndicationTypeButton
            onClick={() => setIndicationType("FOB")}
            type="FOB"
            Icon={<FOBIcon color="rgb(91, 115, 149)" />}
          />
          <IndicationTypeButton
            onClick={() => setIndicationType("DELIVERED")}
            type="DLVD"
            Icon={<DeliveryIcon color="rgb(91, 115, 149)" />}
          />
        </div>
      </div>
    );
  }

  const watchedLocationId = form.watch("indications.0.locationId");
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          fontSize: 18,
          fontWeight: 500,
          paddingTop: 10,
        }}
      >
        {`New ${indicationType === "DELIVERED" ? "DLVD" : "FOB"} Indication`}
      </div>
      <div
        style={{
          flexGrow: 1,
          flexBasis: 0,
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        <form
          onSubmit={form.handleSubmit(logIndication)}
          style={{
            backgroundColor: "white",
            padding: 8,
            borderRadius: 5,
            border: "1px solid #d9d9d9",
          }}
        >
          {form.formState.isSubmitting && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
              }}
            >
              <LoadingIcon color="#005CA6" height={100} width={100} />
            </div>
          )}
          {!form.formState.isSubmitting && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                rowGap: 8,
              }}
            >
              <div>
                <div>Price</div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0px 4px",
                  }}
                >
                  <Controller
                    control={form.control}
                    name="indications.0.prices.0.deliveredPrice"
                    render={({ field }) => (
                      <NumberTextfield
                        sx={
                          indicationType === "DELIVERED"
                            ? {
                                fieldset: {
                                  borderColor: "#005CA6",
                                  borderWidth: 2,
                                },
                              }
                            : undefined
                        }
                        size="small"
                        disabled={indicationType !== "DELIVERED"}
                        value={field.value}
                        setValue={(val) => {
                          if (Number(val) > 999.99) return;

                          changePrice(val, "DELIVERED");
                        }}
                        InputProps={{
                          className: "PriceTextbox",
                          style: {
                            padding: 0,
                            fontSize: 15,
                          },
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              style={{
                                paddingLeft: 3,
                                paddingRight: 1,
                                margin: 0,
                              }}
                            >
                              $
                            </InputAdornment>
                          ),

                          endAdornment: (
                            <InputAdornment
                              position="end"
                              style={{
                                paddingLeft: 1,
                                paddingRight: 3,
                              }}
                            >
                              <div
                                style={{
                                  color:
                                    indicationType === "DELIVERED"
                                      ? "#005CA6"
                                      : undefined,
                                  fontWeight:
                                    indicationType === "DELIVERED" ? 600 : 400,
                                }}
                              >
                                DLVD
                              </div>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      borderBottom: "3px solid #005CA6",
                      paddingBottom: 2,
                      paddingLeft: 3,
                      paddingRight: 4,
                      position: "relative",
                      minWidth: 30,
                      maxWidth: 60,
                    }}
                  >
                    <div style={{ fontSize: 16, fontWeight: 400 }}>
                      <Controller
                        control={form.control}
                        name="indications.0.prices.0.freight"
                        render={({ field }) => (
                          <NumberTextfield
                            size="small"
                            variant="standard"
                            value={field.value}
                            setValue={changeFreight}
                            style={{
                              margin: 0,
                              padding: 0,
                              textAlign: "center",
                            }}
                            InputProps={{
                              style: {
                                fontSize: 14,
                                display: "flex",
                                alignItems: "flex-end",
                                justifyContent: "flex-end",
                              },
                              startAdornment: <div>$</div>,
                            }}
                          />
                        )}
                      />
                    </div>
                    <div
                      style={{
                        color: "#474747",
                        fontSize: 12,
                        fontWeight: 400,
                      }}
                    >
                      Freight
                    </div>
                    <div
                      style={{ position: "absolute", bottom: -20, left: -13 }}
                    >
                      <ArrowLeftIcon
                        style={{ color: "#005CA6", fontSize: 30 }}
                      />
                    </div>
                  </div>
                  <Controller
                    control={form.control}
                    name="indications.0.prices.0.fobPrice"
                    render={({ field }) => (
                      <NumberTextfield
                        sx={
                          indicationType === "FOB"
                            ? {
                                fieldset: {
                                  borderColor: "#005CA6",
                                  borderWidth: 2,
                                },
                              }
                            : undefined
                        }
                        size="small"
                        disabled={indicationType !== "FOB"}
                        value={field.value as any}
                        setValue={(val) => {
                          if (Number(val) > 9999.99) return;
                          changePrice(val, "FOB");
                        }}
                        style={{ padding: 0 }}
                        InputProps={{
                          className: "PriceTextbox",
                          style: {
                            padding: 0,
                            fontSize: 15,
                          },
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              style={{
                                margin: 0,
                                paddingLeft: 3,
                                paddingRight: 1,
                              }}
                            >
                              $
                            </InputAdornment>
                          ),

                          endAdornment: (
                            <InputAdornment
                              position="end"
                              style={{
                                margin: 0,
                                paddingLeft: 1,
                                paddingRight: 3,
                              }}
                            >
                              <div
                                style={{
                                  color:
                                    indicationType === "FOB"
                                      ? "#005CA6"
                                      : undefined,
                                  fontWeight:
                                    indicationType === "FOB" ? 600 : 400,
                                }}
                              >
                                FOB
                              </div>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </div>

                {(form.formState.errors as any)[
                  "indications[0].prices[0].fobPrice"
                ] && (
                  <div
                    style={{ color: "#C52F00", fontSize: 10, textAlign: "end" }}
                  >
                    {
                      (form.formState.errors as any)[
                        "indications[0].prices[0].fobPrice"
                      ].message
                    }
                  </div>
                )}
              </div>

              <div
                style={{
                  border: "1px solid #d9d9d9",
                }}
              />

              <ListField
                label="Contact"
                errorMessage={form.formState.errors.contactName?.message as any}
              >
                <Controller
                  control={form.control}
                  name="contactName"
                  render={({ field }) => (
                    <TextField
                      size="small"
                      fullWidth
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </ListField>

              <ListField
                label="Company"
                errorMessage={form.formState.errors.party?.message as any}
              >
                <Controller
                  control={form.control}
                  name="party"
                  render={({ field }) => (
                    <CompanyFilter
                      value={field.value}
                      setValue={(value) => {
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </ListField>

              <ListField
                label="Competitor"
                errorMessage={form.formState.errors.party?.message as any}
              >
                <Controller
                  control={form.control}
                  name="indications.0.competitor"
                  render={({ field }) => (
                    <CompanyFilter
                      locationId={watchedLocationId}
                      placeholder="Competitor company name"
                      value={field.value}
                      setValue={(value) => {
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </ListField>

              <ListField
                label="Quantity"
                errorMessage={
                  (form.formState.errors as any)["indications[0].quantity"]
                    ?.message as any
                }
              >
                <Controller
                  control={form.control}
                  name="indications.0.quantity"
                  render={({ field }) => (
                    <NumberTextfield
                      fullWidth
                      size="small"
                      value={field.value}
                      setValue={field.onChange}
                    />
                  )}
                />
              </ListField>

              <ListField
                label="Ship Window"
                labelWidth={110}
                errorMessage={
                  ((form.formState.errors as any)["indications[0].startDate"]
                    ?.message as any) ||
                  ((form.formState.errors as any)["indications[0].endDate"]
                    ?.message as any)
                }
              >
                <div
                  style={{
                    display: "flex",
                    gap: 20,
                    width: "100%",
                  }}
                >
                  <Controller
                    control={form.control}
                    name="indications.0.startDate"
                    render={({ field }) => (
                      <MonthPicker
                        value={field.value as string}
                        setValue={field.onChange}
                        type="start"
                      />
                    )}
                  />
                  <Controller
                    control={form.control}
                    name="indications.0.endDate"
                    render={({ field }) => (
                      <MonthPicker
                        value={field.value as string}
                        setValue={field.onChange}
                        type="end"
                      />
                    )}
                  />
                </div>
              </ListField>

              <div>
                <div style={{ fontSize: 16 }}>Comments</div>
                <Controller
                  control={form.control}
                  name="indications.0.comments"
                  render={({ field }) => (
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      maxRows={3}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {!disablePostToTeams && (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: "flex-end",
                      alignItems: "center",
                      fontSize: 12,
                      color: "rgb(75,75,75)",
                      fontWeight: 400,
                    }}
                  >
                    <TeamsIcon />
                    Post to Teams
                    <Controller
                      control={form.control}
                      name="indications.0.postToTeams"
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value}
                          value={field.value}
                          onChange={(e, checked) => {
                            field.onChange(checked);
                          }}
                          disabled={form.formState.isSubmitting}
                          style={{ paddingLeft: 0, paddingRight: 0 }}
                        />
                      )}
                    />
                  </div>
                )}

                {disablePostToTeams && (
                  // Temporary button on mobile since authentication to microsoft doesn't work on iphones
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      style={{ paddingLeft: 20, paddingRight: 20 }}
                      variant="contained"
                      onClick={async () => {
                        const record = form.getValues();
                        const indication = record.indications[0];

                        let message = "";

                        message += `Product=${indication.product}\n`;
                        indication.prices.forEach((r) => {
                          if (indication.priceType === "single") {
                            message += `Origin=${r.origin} | Price=$${r.fobPrice}\n`;
                          } else {
                            message += `Origin=${r.origin} | Price=$${r.fobMinPrice} - $${r.fobMaxPrice}\n`;
                          }
                        });
                        if (indication.quantity) {
                          message += `Qty=${indication.quantity}\n`;
                        }
                        message += `Contact=${record.contactName}\n`;
                        message += `Account=${record.party}\n`;
                        message += `Ship Window=${format(
                          new Date(
                            (indication.startDate as string).replaceAll(
                              "-",
                              "/"
                            )
                          ),
                          "MMM yyyy"
                        )} - ${format(
                          new Date(
                            (indication.endDate as string).replaceAll("-", "/")
                          ),
                          "MMM yyyy"
                        )}\n`;
                        message += `Mode=${indication.mode}\n`;
                        message += indication.comments;

                        await navigator.clipboard.writeText(message.toString());
                      }}
                    >
                      Copy to clipboard
                    </Button>
                  </div>
                )}

                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    {form.formState.errors &&
                      Object.keys(form.formState.errors).length > 0 && (
                        <Tooltip
                          title={
                            <div>
                              {Object.keys(form.formState.errors).map(
                                (r: any) => (
                                  <div>
                                    {(form.formState.errors as any)[r].message}
                                  </div>
                                )
                              )}
                            </div>
                          }
                        >
                          <ErrorIcon style={{ color: "#C52F00" }} />
                        </Tooltip>
                      )}
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Button
                      id="newIndicationCancelButton"
                      variant="outlined"
                      onClick={close}
                    >
                      Cancel
                    </Button>
                    <Button
                      id="newIndicationLogButton"
                      type="submit"
                      variant="contained"
                      // onClick={postIndication}
                      style={{ padding: "0px 20px" }}
                    >
                      Log Indication
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default NewIndication;
